import { useAuthenticationStore } from "@stores/authentication";
import { getApiBaseUrl } from "@utilities/base-url";
import { clearUserData, getCsrfToken } from "@utilities/helper";

// Construct base URL
const baseURL = `${
  getApiBaseUrl()?.endsWith("/") ? getApiBaseUrl() : getApiBaseUrl() + "/"
}`;

async function customFetch(
  url: string,
  version: string = "",
  options: RequestInit = {},
): Promise<any> {
  if (!version) {
    version = process.env.GATSBY_NEW_API_VERSION ?? "";
  }
  // Determine if we need to prepend the baseURL
  const isAbsoluteUrl = url.startsWith("http://") || url.startsWith("https://");
  const fullUrl = isAbsoluteUrl ? url : `${baseURL}${version}${url}`;

  const authenticationState = useAuthenticationStore.getState();

  const defaultHeaders: Record<string, string> = {
    Accept: "application/json",
  };
  if (authenticationState.isAuthenticated()) {
    defaultHeaders.Authorization = `Bearer ${authenticationState.getToken()}`;
  }
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    defaultHeaders["X-XSRF-TOKEN"] = csrfToken;
  }

  const config: RequestInit = {
    credentials: "include",
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(fullUrl, {
      ...config,
      signal: AbortSignal.timeout(30000), // 30 seconds timeout
    });

    // Handle 903 error (token expired)
    if (response.status === 903) {
      const data = await response.json().catch(() => null);
      const errorMessage = String(data?.message ?? "").toLowerCase();

      if (errorMessage.includes("token") || errorMessage.includes("expired")) {
        clearUserData();
        window.location.reload();
      }
      throw new Error(data?.message ?? "Session expired");
    }

    const text = await response.text();
    const data = text ? JSON.parse(text) : null;

    if (!response.ok) {
      console.error(`[fetch-client] HTTP error! status: ${response.status}`);
      throw new Error(
        data?.message ||
          data?.error ||
          `Request failed with status code ${response.status}`,
      );
    }

    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
}

// Create convenience methods
export const api = {
  get: async (url: string, options: RequestInit = {}, version: string = "") =>
    await customFetch(url, version, { ...options, method: "GET" }),

  post: async (
    url: string,
    data?: any,
    options: RequestInit = {},
    version: string = "",
  ) =>
    await customFetch(url, version, {
      ...options,
      method: "POST",
      body: data,
    }),

  put: async (
    url: string,
    data?: any,
    options: RequestInit = {},
    version: string = "",
  ) => {
    if (data && data instanceof FormData) {
      data.append("_method", "PUT");
    }
    return await customFetch(url, version, {
      ...options,
      method: "POST",
      body: data,
    });
  },

  delete: async (
    url: string,
    options: RequestInit = {},
    version: string = "",
  ) => await customFetch(url, version, { ...options, method: "DELETE" }),
};

export default api;
